import API from '../../services/api';

export const getSystemBackup = async () => {
    try {
        const response = await API.get('/system/backup', {
            responseType: 'blob', // Important for file download
        });
        return response;
    } catch (error) {
        console.error("Error fetching system backup:", error);
        throw error;
    }
};

export const restoreSystemBackup = async (file) => {
    try {
        // Read file content as JSON
        const text = await file.text();
        const jsonData = JSON.parse(text);

        const response = await API.post('/system/restore', jsonData);
        return response.data;
    } catch (error) {
        console.error("Error restoring system:", error);
        throw error;
    }
};

// ============================================================
// API Configuration Management
// ============================================================

/**
 * Get all API configurations (masked for display)
 */
export const getAllApiConfigs = async () => {
    try {
        const response = await API.get('/system/api-config');
        return response.data;
    } catch (error) {
        console.error("Error fetching all API configs:", error);
        throw error;
    }
};

/**
 * Get API configuration for a specific category
 * @param {string} category - One of: leegality, imagekit, sandbox, webhook
 */
export const getApiConfig = async (category) => {
    try {
        const response = await API.get(`/system/api-config/${category}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching ${category} config:`, error);
        throw error;
    }
};

// ============================================================
// Database Environment Management
// ============================================================

/**
 * List the database environments the SERVER has configured.
 * Returns masked descriptors only (cluster + database name) - connection
 * strings are never sent to the browser.
 */
export const getDbEnvironments = async () => {
    try {
        const response = await API.get('/system/db-environments');
        return response.data;
    } catch (error) {
        console.error("Error fetching database environments:", error);
        throw error;
    }
};

/**
 * Switch the active database by environment NAME (e.g. 'prodCurrent').
 * The browser never sends a connection string.
 * @param {string} env
 */
export const switchDbEnvironment = async (env) => {
    try {
        const response = await API.put('/system/active-db', { env });
        return response.data;
    } catch (error) {
        console.error("Error switching database environment:", error);
        throw error;
    }
};

// ============================================================
// Generic Configuration Management
// ============================================================

/**
 * Get generic configuration for a specific key
 * @param {string} key
 */
export const getGenericConfig = async (key) => {
    try {
        const response = await API.get(`/system/config/${key}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching ${key} config:`, error);
        throw error;
    }
};

/**
 * Update generic configuration for a specific key
 * @param {string} key
 * @param {any} value - Configuration value to update
 */
export const updateGenericConfig = async (key, value) => {
    try {
        const response = await API.put(`/system/config/${key}`, { value });
        return response.data;
    } catch (error) {
        console.error(`Error updating ${key} config:`, error);
        throw error;
    }
};

/**
 * Update API configuration for a specific category
 * @param {string} category - One of: leegality, imagekit, sandbox, webhook
 * @param {object} configData - Configuration data to update
 */
export const updateApiConfig = async (category, configData) => {
    try {
        const response = await API.put(`/system/api-config/${category}`, configData);
        return response.data;
    } catch (error) {
        console.error(`Error updating ${category} config:`, error);
        throw error;
    }
};

// ============================================================
// Website Deployment (auriksha.com Render deploy control)
// ============================================================

/** Get the website deploy schedule config (hook returned masked, never in full). */
export const getWebsiteDeploymentConfig = async () => {
    const response = await API.get('/website-deployment/config');
    return response.data; // { success, data }
};

/** Update schedule config. Pass deployHook only to set/rotate it; '' clears it. */
export const updateWebsiteDeploymentConfig = async (payload) => {
    const response = await API.put('/website-deployment/config', payload);
    return response.data;
};

/** Trigger an immediate website deploy. */
export const deployWebsiteNow = async () => {
    const response = await API.post('/website-deployment/deploy-now');
    return response.data;
};

/** Recent deploy history. */
export const getWebsiteDeploymentLogs = async (limit = 20) => {
    const response = await API.get('/website-deployment/logs', { params: { limit } });
    return response.data;
};

// ============================================================
// DigiLocker webhook (API Configuration → Sandbox)
// ============================================================

/** Webhook URL to paste into Sandbox Console + delivery health for the last 7 days. */
export const getDigilockerWebhookHealth = async () => {
    const response = await API.get('/system/digilocker-webhook/health');
    return response.data;
};

/** Sign a test event with the stored secret and POST it to our public webhook URL. */
export const sendDigilockerWebhookTest = async () => {
    const response = await API.post('/system/digilocker-webhook/test');
    return response.data;
};

// ============================================================
// Leegality eSign webhook (API Configuration → Webhook)
// ============================================================

/** URLs to set on the Leegality workflow + delivery health for the last 7 days. */
export const getESignWebhookHealth = async () => {
    const response = await API.get('/system/esign-webhook/health');
    return response.data;
};

/** POST a MAC-signed test webhook to our public eSign webhook URL. */
export const sendESignWebhookTest = async () => {
    const response = await API.post('/system/esign-webhook/test');
    return response.data;
};
